import React, { ComponentProps, FC } from 'react';
import styled, { css } from 'styled-components';
import { PrismicImage } from '../../components/image/prismicImage/PrismicImage';
import { Panel } from '../../design/containers/panel/Panel';
import { SliceContainer } from '../../design/containers/sliceContainer/SliceContainer';
import { breakpointSmall } from '../../design/responsive/breakpoints';
import Spacing, { spacings } from '../../design/spacing/Spacing';
import { BodyP, Heading2 } from '../../design/typography/Typography';
import { WithClassName } from '../../utlityTypes';
import QuoteCard from './QuoteCard/QuoteCard';

const LeftImageDesktop = styled(PrismicImage)`
  position: absolute;
  border-radius: ${spacings['12px']};
  left: 0;
`;
LeftImageDesktop.defaultProps = { visibility: { base: 'hide', breakpointMedium: 'show' } };

const LeftImageMobile = styled(PrismicImage)`
  position: absolute;
  max-height: 500px;
  top: 0;
`;

LeftImageMobile.defaultProps = { visibility: { base: 'show', breakpointMedium: 'hide' } };

const BelowRightCardImageDesktop = styled(PrismicImage)`
  max-width: 400px;
`;
BelowRightCardImageDesktop.defaultProps = { visibility: { base: 'hide', breakpointMedium: 'show' } };

const BelowRightCardImageMobile = styled(PrismicImage)`
  object-fit: contain;
`;
BelowRightCardImageMobile.defaultProps = { visibility: { base: 'show', breakpointMedium: 'hide' } };

const StyledSliceContainer = styled(SliceContainer)`
  padding: 0;
`;

const MainContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacings['24px']};
  ${breakpointSmall(
    css`
      grid-template-columns: 1fr 1fr;
      margin: auto;
      height: 100%;
    `
  )};
`;

const CenteredContentContainer = styled.div`
  padding: ${spacings['24px']};
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  ${breakpointSmall(css`
    width: 100%;
    min-height: 1000px;
    padding-top: 0;
  `)}
`;

const LeftCardOuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LeftCardContainer = styled(Panel)`
  padding: ${spacings['24px']};
  margin-top: 350px;
  height: 100% fit-content;
  border-radius: ${spacings['12px']};
  ${breakpointSmall(css`
    padding: ${spacings['48px']};
    margin-top: 0;
    max-width: 630px;
  `)}
`;

type LeftCardProps = Pick<TwoColFloatingContentProps, 'leftCardTitle' | 'leftCardText'>;

const LeftCard = ({ leftCardTitle, leftCardText }: LeftCardProps) => (
  <LeftCardOuterContainer>
    <LeftCardContainer background="whiteSolid">
      <Spacing bottom="24px">
        <Heading2 textAlign="left">{leftCardTitle}</Heading2>
      </Spacing>
      <BodyP type="bodyLRegular">{leftCardText}</BodyP>
    </LeftCardContainer>
  </LeftCardOuterContainer>
);

const RightSideContainer = styled.div``;

type TwoColFloatingContentProps = {
  leftImage: ComponentProps<typeof PrismicImage>['image'];
  leftImageMobile: ComponentProps<typeof PrismicImage>['image'];
  leftCardTitle?: string;
  leftCardText?: string;
  rightCardQuote?: string;
  rightCardAuthor?: string;
  rightCardJobTitle?: string;
  rightCardCompany?: string;
  belowRightCardImage: ComponentProps<typeof PrismicImage>['image'];
  belowRightCardImageMobile: ComponentProps<typeof PrismicImage>['image'];
} & WithClassName;

/**
 * Two Col Floating Content slice.
 */
const TwoColFloatingContent: FC<TwoColFloatingContentProps> = ({
  leftImage,
  leftImageMobile,
  leftCardTitle,
  leftCardText,
  rightCardQuote,
  rightCardAuthor,
  rightCardJobTitle,
  rightCardCompany,
  belowRightCardImage,
  belowRightCardImageMobile,
}) => (
  <StyledSliceContainer background="brand5Solid">
    <CenteredContentContainer>
      <LeftImageDesktop image={leftImage} />
      <LeftImageMobile image={leftImageMobile} />
      <MainContainer>
        <LeftCard leftCardTitle={leftCardTitle} leftCardText={leftCardText} />
        <RightSideContainer>
          <QuoteCard
            background="brand3SolidStripedCurve"
            quote={rightCardQuote}
            author={rightCardAuthor}
            role={rightCardJobTitle}
            company={rightCardCompany}
          />
          {belowRightCardImage && <BelowRightCardImageDesktop image={belowRightCardImage} />}
          {belowRightCardImageMobile && <BelowRightCardImageMobile image={belowRightCardImageMobile} />}
        </RightSideContainer>
      </MainContainer>
    </CenteredContentContainer>
  </StyledSliceContainer>
);

export default TwoColFloatingContent;
